import { t } from "@/localization";
import { fetchAuthSessionWithLoginMismatchRetry } from "../../utils/amplifyAuthRecovery";
import { evtLiveOwnerHeaders } from "../teams/workspace";
import { UploadV2Error } from "./core";

// evt.live Upload Service v2 (base /api/uploads/v2). create and list use the
// signed-in broadcaster's Cognito access token; everything about one session
// uses the X-Upload-Token returned by create (or the account token when there
// is none). In someone else's account (Teams) the account-token requests also
// name the owner (x-el-owner): evt.live lets editors add video and admins
// replace it. Errors are UploadV2Error with a short message for people, the
// HTTP status (0 = network or timeout) and evt.live's stable `code` when it
// sends one ("http" otherwise).

const TIMEOUTS_MS = {
  create: 30000,
  parts: 30000,
  status: 15000,
  // Completing assembles up to 9000 parts on B2: allow it time.
  complete: 120000,
  abort: 15000,
  list: 15000,
  retry: 30000,
};

// evt.live's error codes → what people see. Unknown codes fall back to the
// API's own short `error` text.
export const ERROR_MESSAGES = {
  event_live: t("Your event is live. Upload after the stream ends."),
  event_not_found: t("This event no longer exists."),
  forbidden: t("You don't have access to upload video to this event."),
  unauthorized: t("Please sign in again."),
  token_expired: t("This upload has expired. Please choose the file again."),
  token_invalid: t("This upload has expired. Please choose the file again."),
  not_uploading: t("This upload has already finished or was cancelled."),
  missing_parts: t("Some parts of the video haven't uploaded yet."),
  size_mismatch: t(
    "The uploaded video doesn't match the file. Please upload it again."
  ),
  upload_expired: t("This upload has expired. Please choose the file again."),
  duplicate: t("This video was already uploaded for this event."),
  too_many_uploads: t(
    "Too many uploads are in progress. Finish or cancel one, then try again."
  ),
  too_large: t("This file is too large. Videos can be up to 200 GB."),
  empty_file: t("This file is empty. Please choose another video."),
  unsupported_type: t("Please choose a video file (MP4 or MOV)."),
  invalid_request: t(
    "This upload couldn't be started. Please choose the file again."
  ),
  rate_limited: t("Too many requests. Please wait a moment and try again."),
  unavailable: t(
    "Video upload is temporarily unavailable. Please try again later."
  ),
  internal: t("Something went wrong on our side. Please try again."),
  not_retryable: t(
    "This video can't be converted again. Please upload it again."
  ),
};
// Worth trying again by itself.
const TRANSIENT_CODES = new Set(["rate_limited", "unavailable", "internal"]);

const errorCode = (data) =>
  typeof data?.code === "string" && /^[a-z_]{1,40}$/.test(data.code)
    ? data.code
    : null;

const STATUS_MESSAGES = {
  401: t("Please sign in again."),
  403: t("You don't have access to upload video to this event."),
  404: t("This upload is no longer available. Please choose the file again."),
  409: t("This upload can't continue. Please choose the file again."),
  413: t("This file is too large. Videos can be up to 200 GB."),
  415: t("Please choose a video file (MP4 or MOV)."),
  422: t("This file can't be uploaded. Please choose another video."),
  429: t("Too many uploads at once. Please wait a moment and try again."),
};
const UNAVAILABLE_MESSAGE = t(
  "Video upload is temporarily unavailable. Please try again later."
);
const OFFLINE_MESSAGE = t(
  "Could not reach the upload service. Check your internet connection and try again."
);

// Show the API's own short, plain-text errors ("Your event is live. Upload
// after the stream ends."). HTML pages, stack traces or long text get a
// friendly message instead.
const apiErrorMessage = (data) => {
  const message =
    data && typeof data.error === "string" ? data.error.trim() : "";
  return message && message.length <= 200 && !/[<>{}]/.test(message)
    ? message
    : "";
};

export const getUploadAccessToken = async () => {
  try {
    const session = await fetchAuthSessionWithLoginMismatchRetry();
    return session?.tokens?.accessToken?.toString() || null;
  } catch (error) {
    return null;
  }
};

const sessionPath = (id) => `/${encodeURIComponent(id)}`;

// GET ?slug= returns the event's last 20 sessions; accept a bare array or a
// wrapping object.
const listFrom = (data) => {
  if (Array.isArray(data)) return data;
  for (const key of ["uploads", "sessions", "items"]) {
    if (Array.isArray(data?.[key])) return data[key];
  }
  return [];
};

export function createUploadApi({
  baseUrl,
  getAccessToken = getUploadAccessToken,
  fetchImpl,
  timeouts = {},
  getOwnerHeaders = evtLiveOwnerHeaders,
} = {}) {
  const base = String(baseUrl || "").replace(/\/+$/, "");
  const limits = { ...TIMEOUTS_MS, ...timeouts };
  const send = (...args) => (fetchImpl || fetch)(...args);

  async function request(
    path,
    { method = "GET", body, uploadToken, owner = false, slug, timeoutMs, stage }
  ) {
    const headers = { Accept: "application/json" };
    if (body !== undefined) headers["Content-Type"] = "application/json";
    if (uploadToken) headers["X-Upload-Token"] = uploadToken;
    if (owner) {
      const token = await getAccessToken();
      if (!token) {
        throw new UploadV2Error(STATUS_MESSAGES[401], {
          status: 401,
          code: "auth",
          stage,
        });
      }
      headers.Authorization = `Bearer ${token}`;
      Object.assign(headers, getOwnerHeaders(slug));
    }

    const controller =
      typeof AbortController !== "undefined" ? new AbortController() : null;
    const timer = controller
      ? setTimeout(() => controller.abort(), timeoutMs)
      : null;

    let response;
    let data = null;
    try {
      try {
        response = await send(`${base}${path}`, {
          method,
          headers,
          ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
          ...(controller ? { signal: controller.signal } : {}),
        });
      } catch (error) {
        throw new UploadV2Error(OFFLINE_MESSAGE, {
          status: 0,
          code: controller?.signal.aborted ? "timeout" : "network",
          retryable: true,
          stage,
        });
      }
      try {
        const text = await response.text();
        data = text ? JSON.parse(text) : null;
      } catch (error) {
        // Non-JSON (an HTML error page) or a body cut off by the timeout.
        data = null;
      }
    } finally {
      if (timer) clearTimeout(timer);
    }

    if (!response.ok) {
      const status = response.status;
      const code = errorCode(data);
      throw new UploadV2Error(
        ERROR_MESSAGES[code] ||
          apiErrorMessage(data) ||
          STATUS_MESSAGES[status] ||
          UNAVAILABLE_MESSAGE,
        {
          status,
          code: code || "http",
          retryable: code
            ? TRANSIENT_CODES.has(code)
            : status === 408 || status === 429 || status >= 500,
          stage,
          missingParts: Array.isArray(data?.missingParts)
            ? data.missingParts
            : null,
          existing:
            data?.existing && typeof data.existing === "object"
              ? data.existing
              : null,
        }
      );
    }
    if (!data || typeof data !== "object") {
      throw new UploadV2Error(UNAVAILABLE_MESSAGE, {
        status: response.status,
        code: "bad_response",
        retryable: true,
        stage,
      });
    }
    return data;
  }

  return {
    // { slug, filename, size, contentType, lastModified?, mode?, insertIndex?,
    //   notifyGuests?, client?, force? } → { id, uploadToken, partSize,
    //   partCount, expiresAt, resumed, uploadedParts }. 409 code "duplicate"
    //   (error.existing = { id, status, createdAt, mode }) when the same file
    //   was handed over in the last 24 h; force: true uploads it again.
    create: (input) =>
      request("", {
        method: "POST",
        body: input,
        owner: true,
        slug: input?.slug,
        timeoutMs: limits.create,
        stage: "create",
      }),

    // → { urls: { "1": "https://…" }, expiresAt }
    signParts: (id, uploadToken, partNumbers, ttlSeconds) =>
      request(`${sessionPath(id)}/parts`, {
        method: "POST",
        body: { partNumbers, ...(ttlSeconds ? { ttlSeconds } : {}) },
        uploadToken,
        timeoutMs: limits.parts,
        stage: "sign",
      }),

    // With the upload token, or as the event owner when there is none.
    status: (id, uploadToken) =>
      request(sessionPath(id), {
        uploadToken,
        owner: !uploadToken,
        timeoutMs: limits.status,
        stage: "status",
      }),

    complete: (id, uploadToken) =>
      request(`${sessionPath(id)}/complete`, {
        method: "POST",
        body: {},
        uploadToken,
        timeoutMs: limits.complete,
        stage: "complete",
      }),

    abort: (id, uploadToken) =>
      request(`${sessionPath(id)}/abort`, {
        method: "POST",
        body: {},
        uploadToken,
        timeoutMs: limits.abort,
        stage: "abort",
      }),

    // Queue a failed conversion again (no re-upload) → { status: "queued" }.
    retry: (id, uploadToken) =>
      request(`${sessionPath(id)}/retry`, {
        method: "POST",
        body: {},
        uploadToken,
        owner: !uploadToken,
        timeoutMs: limits.retry,
        stage: "retry",
      }),

    list: async (slug) =>
      listFrom(
        await request(`?slug=${encodeURIComponent(slug)}`, {
          owner: true,
          slug,
          timeoutMs: limits.list,
          stage: "list",
        })
      ),
  };
}
